import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import DocumentUploadArea from '../../common/DocumentUploadArea/DocumentUploadArea';
import ChatWidget from '../../common/ChatWidget/ChatWidget';
import { Clock, Calendar, SquareCheckBig, Banknote } from 'lucide-react';
import { useNotifications } from '@/hooks/useNotifications';

const TramiteManagementPage = () => {
  const { success, error } = useNotifications();

  const { tramiteRequestId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tramiteRequest, setTramiteRequest] = useState(null);
  const [tramiteType, setTramiteType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [documentsReady, setDocumentsReady] = useState(false);
  const [chatVisible, setChatVisible] = useState(true); // Chat siempre visible por defecto

  useEffect(() => {
    if (tramiteRequestId) {
      fetchTramiteInfo();
    }
  }, [tramiteRequestId]);

  const fetchTramiteInfo = async () => {
    try {
      const token = localStorage.getItem('token');

      // Obtener información de la solicitud de trámite
      const requestRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/applications/my-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (requestRes.ok) {
        const requests = await requestRes.json();
        const currentRequest = requests.find(r => r.id === tramiteRequestId);

        if (currentRequest) {
          setTramiteRequest(currentRequest);

          // Obtener todas las categorías para encontrar el tipo de trámite
          const categoriesRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/categories`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (categoriesRes.ok) {
            const categories = await categoriesRes.json();
            let foundTramiteType = null;

            // Buscar el tipo de trámite en todas las categorías
            for (const category of categories) {
              const tramitesRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/categories/${category.id}/tramites`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });

              if (tramitesRes.ok) {
                const tramites = await tramitesRes.json();
                const tramiteType = tramites.find(t => t.id === currentRequest.tramite_type_id);
                if (tramiteType) {
                  foundTramiteType = tramiteType;
                  break;
                }
              }
            }

            if (foundTramiteType) {
              setTramiteType(foundTramiteType);
            } else {
              // Fallback: crear un tipo de trámite genérico para testing
              console.warn('Tramite type not found, using fallback');
              setTramiteType({
                id: currentRequest.tramite_type_id,
                name: 'Trámite Genérico',
                description: 'Trámite en proceso de gestión',
                price_type: 'variable',
                estimated_time_days: 15,
                required_documents: ['Documentos requeridos']
              });
            }
          }
        } else {
          // Solicitud no encontrada
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Error fetching tramite info:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentsChange = (documentsStatus) => {
    setDocumentsReady(documentsStatus.all_approved);
  };

  const handleAllDocsApproved = async () => {
    success('¡Excelente! Todos tus documentos han sido aprobados. Tu trámite está listo para cotización.');

    // Chat automático de felicitación a través del sistema de notificaciones
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/chat/send-system-notification?application_id=${tramiteRequestId}&message=${encodeURIComponent(`¡Felicidades ${user?.name}! Todos los documentos para tu trámite "${tramiteType?.name}" han sido aprobados <SquareCheckBig /> Tu solicitud está ahora lista para cotización. Nuestro equipo procesará la cotización y te contactaremos pronto.`)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Info: Notificación de chat no enviada:', error);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Cargando información del trámite..." />;
  }

  if (!tramiteRequest || !tramiteType) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Trámite no encontrado</h2>
          <p className="text-gray-600 mb-6">La solicitud de trámite no existe o no tienes acceso.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-4 font-medium"
          >
            ← Volver al Dashboard
          </button>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-gray-900 mb-4">
                  <Clipboard /> Gestión: {tramiteType.name}
                </h1>
                <p className="text-gray-600 text-lg mb-4">
                  {tramiteType.description}
                </p>

                {/* Estado del trámite */}
                <div className="flex items-center space-x-4 mb-6">
                  <span className={`px-4 py-2 rounded-full text-sm font-medium gap-2 flex items-center ${tramiteRequest.status === 'completado' ? 'bg-green-100 text-green-800' :
                    tramiteRequest.status === 'en_proceso' ? 'bg-blue-100 text-blue-800' :
                      tramiteRequest.status === 'documentos_rechazados' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                    }`}>
                    Estado: {tramiteRequest.status}
                  </span>
                  <span className="text-sm text-gray-500">
                    <Calendar /> Solicitado: {new Date(tramiteRequest.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              {/* Info rápida */}
              <div className="text-right">
                <div className="space-y-2 text-sm">
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2"><Banknote /></span>
                    <span className="font-medium">
                      {tramiteType.price_type === 'fijo'
                        ? `$${tramiteType.fixed_price} MXN`
                        : 'Cotización pendiente'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2"><Clock className="mr-1" /></span>
                    <span className="font-medium">{tramiteType.estimated_time_days} días</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Área de documentos */}
        <DocumentUploadArea
          tramiteRequestId={tramiteRequestId}
          onDocumentsChange={handleDocumentsChange}
          onAllApproved={handleAllDocsApproved}
        />

        {/* Chat inline con Socket.IO */}
        <div className="mt-8 bg-white rounded-2xl shadow-lg p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              <MessageCircle /> Chat en Tiempo Real con tu Gestor
            </h2>

            <button
              onClick={() => setChatVisible(!chatVisible)}
              className={`px-4 py-2 rounded-lg transition-colors ${chatVisible
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              {chatVisible ? '🔽 Ocultar Chat' : '🔼 Mostrar Chat'}
            </button>
          </div>

          {chatVisible ? (
            <div className="border border-gray-200 rounded-lg">
              <ChatWidget
                tramiteRequestId={tramiteRequestId}
                isVisible={true}
                onToggleVisibility={() => setChatVisible(false)}
                tramiteName={tramiteType?.name}
              />
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
              <div className="text-4xl mb-4"><MessageCircle /></div>
              <h3 className="text-lg font-medium text-blue-900 mb-2">
                Chat Oculto
              </h3>
              <p className="text-blue-700 mb-4">
                El chat en tiempo real está disponible. Haz clic para mostrarlo.
              </p>
              <button
                onClick={() => setChatVisible(true)}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                🔼 Mostrar Chat
              </button>
            </div>
          )}
        </div>

        {/* Widget de chat simplificado (respaldo) */}
        <ChatWidget
          tramiteRequestId={tramiteRequestId}
          isVisible={false} // Deshabilitado porque usamos chat inline
          onToggleVisibility={() => { }}
          tramiteName={tramiteType?.name}
        />

        {/* Información adicional */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Próximos pasos */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              <Clipboard /> Próximos Pasos
            </h3>
            <div className="space-y-3">
              <div className={`flex items-center p-3 rounded-lg ${documentsReady ? 'bg-green-50 text-green-800' : 'bg-gray-50 text-gray-600'
                }`}>
                <span className="text-xl mr-3">
                  {documentsReady ? <SquareCheckBig /> : '📄'}
                </span>
                <span className="font-medium">
                  {documentsReady ? 'Documentos aprobados' : 'Subir y aprobar documentos'}
                </span>
              </div>

              <div className={`flex items-center p-3 rounded-lg ${documentsReady ? 'bg-blue-50 text-blue-800' : 'bg-gray-50 text-gray-400'
                }`}>
                <span className="text-xl mr-3"><Banknote /></span>
                <span className="font-medium">Recibir cotización</span>
              </div>

              <div className="flex items-center p-3 rounded-lg bg-gray-50 text-gray-400">
                <span className="text-xl mr-3">🔄</span>
                <span className="font-medium">Proceso de gestión</span>
              </div>

              <div className="flex items-center p-3 rounded-lg bg-gray-50 text-gray-400">
                <span className="text-xl mr-3">✨</span>
                <span className="font-medium">Trámite completado</span>
              </div>
            </div>
          </div>

          {/* Información de contacto */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              📞 Información de Contacto
            </h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <span className="text-xl mr-3">📱</span>
                <div>
                  <p className="font-medium text-gray-900">WhatsApp</p>
                  <p className="text-sm text-gray-600">+52 558 535 3729</p>
                </div>
              </div>

              <div className="flex items-center">
                <span className="text-xl mr-3">📧</span>
                <div>
                  <p className="font-medium text-gray-900">Email</p>
                  <p className="text-sm text-gray-600">contacto@mistramites.mx</p>
                </div>
              </div>

              <div className="flex items-center">
                <span className="text-xl mr-3">🕐</span>
                <div>
                  <p className="font-medium text-gray-900">Horario</p>
                  <p className="text-sm text-gray-600">Lun-Vie 9:00-18:00</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TramiteManagementPage;